import React, { useContext, useEffect, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { useHistory } from 'react-router-dom';

import {
	ListDefaults,
	IApp04Topic,
	IApp04AIChat,
	IEntityDescription1
} from '../types';
import { EntityList } from './EntityList';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import { ErrorBoundary } from './ErrorBoundary';
import { IWithErrorHandlingProps, withErrorHandling } from './withErrorHandling';
import { IUseApiProps } from './useApi';
import { renderAsLink, renderDate, renderDateRange, renderDateRangeSimple, renderIndicators, renderPortfolioIcons, renderStage } from './cr/ListRenderers';
import { DataContext } from './DataContext';
import { IntegrationContext } from './IntegrationContext';
import { OrbUserContext } from './OrbUserContext';
import { useParams } from 'react-router-dom';
import { IColumn } from '@fluentui/react/lib/DetailsList';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import useMobileDetector from './useMobileDetector';
import { DateService } from '../services';
import { AppIDs } from '../AppGlobals';
import { AiChatForm } from './AI/AiChatForm';
import { App04TopicForm } from './AI/App04TopicForm';
import { MessageDialog } from './cr/MessageDialog';
import { ConfirmDialog } from './cr/ConfirmDialog';

export interface IApp04CloeAIAdminProps extends IUseApiProps, IWithErrorHandlingProps {
	entity: string;
	menuData: any[];

	lastTopicIdForChilds?: number | null;
	setLastTopicIdForChilds?: React.Dispatch<React.SetStateAction<number | null>>;

	onEntityDescriptionChange: (desc: IEntityDescription1) => void;
}


const App04CloeAIAdmin = (props: IApp04CloeAIAdminProps) => {
	const { entity } = props;
	const { dataServices } = useContext(DataContext);
	const { orbConfig, userContext, userPermissions } = useContext(OrbUserContext);
	const isMobile = useMobileDetector();
	const { params } = useRouteMatch();
	const history = useHistory();
	const lcc = ListDefaults.columnWidths;
	const { topicIdForChats } = useParams<{ topicIdForChats: string }>();

	const [chatsForCounters, setChatsForCounters] = useState<IApp04AIChat[]>([]);
	const [chatsRefreshKey, setChatsRefreshKey] = useState(0);
	const [chatPendingClear, setChatPendingClear] = useState<IApp04AIChat | null>(null);

	const topicNameWidths = isMobile ? { minWidth: 90, maxWidth: 150 } : { minWidth: 150, maxWidth: 250 };

	const getPreFarItems = (keys: string[]) => {
		const config: Record<string, ICommandBarItemProps> = {
			Topics: {
				key: 'Topics',
				name: 'Back to Topics',
				iconProps: { iconName: 'Back' },
				onClick() {
					props.setLastTopicIdForChilds(null);
					history.push(`/a14/topics`);
				},
			},

			// Add more configurations as needed for other keys
		};

		const arr: ICommandBarItemProps[] = keys
			.filter((key) => config[key]) // Only include valid keys
			.map((key) => config[key]);

		return arr;
	};

	const openChatsForTopic = (topicID?: number) => {
		props.setLastTopicIdForChilds(topicID);
		history.push(`/a14/chats-for-topic/${topicID}`);
	};

	const openAIChat = (chatID?: number) => {
		const SALT = "zedxcloe";
		const encoded = btoa(`${SALT}_${chatID}_${SALT}`);
		history.push(`/a14/ask-cloe/${encoded}`);
	};

	const confirmClearChat = (): void => {
		if (chatPendingClear) {
			dataServices.app04AIChatService
				.clearChatMessages({ ChatID: chatPendingClear.ID })
				.then(() => {
					setChatPendingClear(null);
					setChatsRefreshKey(k => k + 1);
				});
		}
	};

	const cancelClearChat = (): void => {
		setChatPendingClear(null);
	};

	return (
		<ErrorBoundary>
			<ConfirmDialog
				hidden={!chatPendingClear}
				title={`Are you sure you want to clear all messages in '${chatPendingClear?.Title}'?`}
				content="This will permanently delete all messages in this chat. This cannot be undone."
				confirmButtonText="Clear"
				handleConfirm={confirmClearChat}
				handleCancel={cancelClearChat}
			/>
			{entity === 'Topics' &&
				<EntityList<IApp04Topic>
					{...props}
					disableAdd={false}
					enableShowHideClosedEntities={true}
					entityName={{ Plural: 'Topics', Singular: 'Topic' }}
					barColorBeforeTitle='#99D9EA'
					entity={"Topics"} //just to reload
					columns={[
						{
							key: '1',
							name: 'Title',
							fieldName: 'Name',
							...topicNameWidths,
							isMultiline: true,
							isResizable: true,
							onRender: item => renderAsLink(item.key, item.Name, (ID: number) => openChatsForTopic(ID))
						},
						{
							key: '2',
							name: 'Data Access',
							fieldName: 'DataAccess',
							minWidth: 170,
							maxWidth: 240,
							isResizable: true,
							isMultiline: true,
							isCollapsible: true,
						},
						{
							key: '3',
							name: 'Web Search',
							fieldName: 'WebSearch',
							minWidth: 170,
							maxWidth: 220,
							isResizable: true,
							isMultiline: true,
							isCollapsible: true,
						},
						{
							key: '4',
							name: 'Chats',
							fieldName: 'Chats',
							minWidth: 70,
							maxWidth: 80,
							isResizable: true,
							isCollapsible: true,
						},
						{
							key: '5',
							name: 'Owners',
							fieldName: 'Owners',
							minWidth: 180,
							maxWidth: 260,
							isResizable: true,
							isMultiline: true,
							isCollapsible: true,
						},
					]}
					loadListItems={async showClosedEntities => {
						const chats: IApp04AIChat[] = await dataServices.app04AIChatService.readAllChatsForCounters(showClosedEntities);
						setChatsForCounters(chats);

						return dataServices.app04TopicService.readAllForList(showClosedEntities);
					}}

					mapEntitiesToListItems={topics => topics.map(t => {

						const app = props.menuData.find(a => a.ID === t.AppID);

						const totalChats: number = chatsForCounters?.filter(c => c.TopicID === t.ID)?.length ?? 0;
						const totalUserChats: number = chatsForCounters?.filter(c => c.TopicID === t.ID && c.App04AIChatUsers.some(u => u.UserID === userContext.UserId))?.length ?? 0;

						return {
							key: t.ID,
							Name: t.Title,

							DataAccess: app
								? `${app.AppName} Data`
								: "No Data Access",

							WebSearch:
								t.WebSearch === "Required"
									? "Search web if required"
									: t.WebSearch === "Never"
										? "Never search the web"
										: t.WebSearch === "Always"
											? "Always search the web"
											: t.WebSearch === "Sites"
												? "Only search specified sites"
												: "",

							Chats: `${totalUserChats}/${totalChats}`,

							Owners: [
								t.App04TopicUsers &&
									t.App04TopicUsers.some(u => u.User != null)
									? t.App04TopicUsers.map(u => u.User?.Title).join(", ")
									: null,
							].filter(Boolean).join(", "),
						};
					})}
					entityForm={(showForm, entityId, onSaved, onCancelled, entityDescription1, entityDescription1DirectorateID, selectedEntity) => {
						const isOwner = selectedEntity?.App04TopicUsers?.some(u => u.UserID === userContext.UserId)
							|| userPermissions.UserIsSystemAdmin();

						if (entityId > 0 && !isOwner) {
							return (
								<MessageDialog
									hidden={!showForm}
									title={`${selectedEntity?.Title} cannot be edited`}
									handleOk={onCancelled}
								>
									<>
										<p>
											You are not an owner of this topic. Only a topic&apos;s owners can make changes to it.
										</p>
									</>
								</MessageDialog>
							);
						} else {
							return (
								<App04TopicForm
									showForm={showForm}
									entityId={entityId}
									onSaved={onSaved}
									onCancelled={onCancelled}
									{...props}
								/>
							);
						}
					}}

					// viewForm={(showForm, entityId, onClosed) =>
					// 	<PortfolioView
					// 		{...props}
					// 		showForm={showForm}
					// 		entityId={entityId}
					// 		entityName='Portfolio'
					// 		loadEntity={portfolioId => dataServices.portfolioService.readForView(portfolioId)}
					// 		onClosed={onClosed}
					// 	/>
					// }

					onCheckDeleteCustomMsg={(entityId, selectedEntity: IApp04Topic) => {
						const isOwner = selectedEntity?.App04TopicUsers?.some(u => u.UserID === userContext.UserId)
							|| userPermissions.UserIsSystemAdmin();

						if (!isOwner) {
							return (
								<p>
									You are not an owner of this topic, so you cannot delete it.
									Only a topic&apos;s owners can delete it.
								</p>
							);
						}

						return null;
					}}
					onCheckDelete={id => dataServices.app04TopicService.entityChildren(id)}
					onDelete={id => dataServices.app04TopicService.delete(id)}
				/>
			}
			{entity === 'AIChats' &&
				<EntityList<IApp04AIChat>
					{...props}
					key={chatsRefreshKey}
					disableAdd={false}
					disableDelete={(chat: IApp04AIChat) => (chat?.App04AIChatMessages?.length ?? 0) > 0}
					preFarItemsOnSelection={(chat: IApp04AIChat) => {
						const hasMessages = (chat?.App04AIChatMessages?.length ?? 0) > 0;

						if (!hasMessages) return [];

						return [
							{
								key: 'ClearChat',
								name: 'Clear',
								iconProps: { iconName: 'Broom' },
								onClick: (ev, item) => {
									setChatPendingClear(chat);
								},
							},
						];
					}}

					enableShowHideClosedEntities={true}
					entityName={{ Plural: 'Chats', Singular: 'Chat' }}
					barColorBeforeTitle='#99D9EA'
					entity={topicIdForChats !== undefined ? "Chats for topic" : "Chats"} //just to reload
					showEntityDescription1={topicIdForChats !== undefined}

					loadEntityDescription1={() => {
						return topicIdForChats !== undefined
							? dataServices.app04TopicService.readTitleForTopic(
								'Chats',
								'Chat',
								Number(topicIdForChats)
							)
							: null;
					}}

					onEntityDescriptionChange={props.onEntityDescriptionChange}
					veryFirstItems={(p) =>
						topicIdForChats !== undefined
							? getPreFarItems(['Topics'])
							: null
					}

					columns={[
						{ key: '1', name: 'Chat Title', fieldName: 'Name', ...topicNameWidths, isMultiline: true, isResizable: true, onRender: item => renderAsLink(item.key, item.Name, (ID: number) => openAIChat(ID)) },
						{ key: '2', name: 'Last Prompt', fieldName: 'LastPrompt', minWidth: 150, maxWidth: 250, isResizable: true, isMultiline: true, },
						{ key: '3', name: 'Prompts', fieldName: 'TotalPrompts', minWidth: 60, maxWidth: 70, isResizable: true, isCollapsible: true },
						{ key: '4', name: 'Owners', fieldName: 'Owners', minWidth: 150, maxWidth: 200, isResizable: true, isCollapsible: true, isMultiline: true },
						{ key: '5', name: 'Dates', minWidth: 100, isResizable: true, isCollapsible: true, onRender: item => renderDateRangeSimple(item.CreatedOn, item.LastPromptAt) },
					]}

					loadListItems={showClosedEntities => {
						if (topicIdForChats !== undefined) {
							props.setLastTopicIdForChilds(Number(topicIdForChats));
						} else {
							props.setLastTopicIdForChilds(null);
						}

						return topicIdForChats !== undefined
							? dataServices.app04AIChatService.readAllForList(showClosedEntities, Number(topicIdForChats))
							: dataServices.app04AIChatService.readAllForList(showClosedEntities);
					}}

					mapEntitiesToListItems={chats => chats.map(c => {

						return {
							key: c.ID,
							Name: c.Title,
							LastPrompt: c.LastPrompt,
							TotalPrompts: c.TotalPrompts,
							Owners: [
								c.App04AIChatUsers && c.App04AIChatUsers.some(cu => cu.User != null) ? `${c.App04AIChatUsers.map(pu => pu.User?.Title).join(', ')}` : null,
							].filter(Boolean).join(', '),
							CreatedOn: c.CreatedOn,
							CreatedOnValue: c.CreatedOn?.valueOf(),
							LastPromptAt: c.LastPromptAt,
							LastPromptAtValue: c.LastPromptAt?.valueOf(),
						};
					})}

					entityForm={(showForm, entityId, onSaved, onCancelled, entityDescription1) =>
						<AiChatForm
							showForm={showForm}
							entityId={entityId}
							showEntityDescription1={topicIdForChats !== undefined}
							entityDescription1={entityDescription1}
							defaultValues={
								topicIdForChats !== undefined
									? [
										{ field: 'TopicID', value: Number(topicIdForChats) },
										{ field: 'SummaryFrequency', value: 10 },
									]
									: [
										{ field: 'SummaryFrequency', value: 10 },
									]
							}
							onSaved={onSaved}
							onCancelled={onCancelled}
							{...props}
						/>
					}

					onCheckDelete={() => Promise.resolve([])}
					onDelete={id => dataServices.app04AIChatService.delete(id)}
				/>
			}


		</ErrorBoundary>
	);

};

export default withErrorHandling(App04CloeAIAdmin);
